import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { updateProfileApi } from "../../Services/updateProfile/updateProfileApi";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { CommunityOwner, HomeOwner, Realtor, Vendor } from "../../Utils/Constant";
import "react-toastify/dist/ReactToastify.css";
import defaultProfilePic from "../../images/default_pfp.svg";
import "./updateProfile.css";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { State, City } from "country-state-city";
import Modal from 'react-bootstrap/Modal';
import ReactCrop from "react-easy-crop";
import { fetchVendorSubTypeList } from "../../Services/Signup/SignupApi/SignupApi";
import { removeUnderScore } from "../../Utils/LetterCase";


// Add this helper function for creating a cropped image
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.src = url;
  });

// Add this helper function for getting the cropped canvas
const getCroppedImg = async (imageSrc, pixelCrop) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  // Set canvas size to desired dimensions
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  // Create a circular clipping path
  ctx.beginPath();
  ctx.arc(
    canvas.width / 2,
    canvas.height / 2,
    Math.min(canvas.width, canvas.height) / 2,
    0,
    2 * Math.PI,
    true
  );
  ctx.clip();

  // Draw the cropped image
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  // Convert canvas to blob
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob);
    }, 'image/jpeg');
  });
};

function UpdateProfile() {
  const [errors, setErrors] = useState({});
  const [profilePic, setProfilePic] = useState(defaultProfilePic); // Default image path
  const [imageFile, setImageFile] = useState(null); // To store the uploaded file
  const [isClicked, setIsClicked] = useState(false); // State to track click status
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState("");

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    image: null,
    address: "",
    state: "",
    city: "",
    zipcode: "",
    brokerageName: "",
    sub_vendor: "",
  });
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const profileData = React.useMemo(() => location.state?.profileData || {}, [location.state]);
  const [showCropModal, setShowCropModal] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isSaving, setIsSaving] = useState(false)
  const [loading, setLoading] = useState(false);

  const vendorMaster = {
    health: "Health",
    // education: "Education",
    local_vendor: "Local Vendor",
    community_expert: "Community Expert",
  };

  const vendor = useSelector((state) => state.vendorSubtype.vendorSubtypes) || {};
  const subVendors = vendor || [];
  // const loading = useSelector((state) => state.vendorSubtype.loading);
  const error = useSelector((state) => state.vendorSubtype.error);


  const userType = localStorage.getItem("user_type");
  // Prefill the form with profileData when the component mounts
  useEffect(() => {
    if (profileData) {
      setFormData({
        firstName: profileData.first_name || "",
        lastName: profileData.last_name || "",
        phoneNumber: profileData.phone_number || "",
        image: profileData.image || null,
        address: profileData.address || "",
        state: profileData.state || "",
        city: profileData.city || "",
        zipcode: profileData.zipcode || "",
        brokerageName: profileData.brokerage_name || "",
        sub_vendor: profileData.sub_vendor || "",
        vendor: profileData.vendor || "",
      });
      setCities(City.getCitiesOfState("US", profileData?.state));
    }

    if (profileData.vendor) {
      setSelectedVendor(profileData.vendor);
      dispatch(fetchVendorSubTypeList({ vendor_master_name: profileData.vendor }));
    }

  }, [profileData, dispatch]);


  useEffect(() => {
    const usaStates = State.getStatesOfCountry("US");
    setStates(usaStates);
  }, []);

  // Handle input changes
  const handleChange = (event) => {
    const { name, value, files } = event.target;
    const fieldValue = files ? files[0] : value;

    // Update formData
    setFormData((prevData) => ({
      ...prevData,
      [name]: fieldValue,
    }));

    // Validate the field and update errors
    const error = validateField(name, fieldValue);
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error,
    }));
  };

  const handlePhoneNumberChange = (e) => {
    let value = e.target.value.replace(/\D/g, ""); // Remove non-numeric characters

    if (value.length <= 3) {
      // Do nothing, value remains the same
    } else if (value.length <= 6) {
      value = `(${value.slice(0, 3)}) ${value.slice(3)}`;
    } else {
      value = `(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(
        6,
        10
      )}`;
    }

    setFormData((prevData) => ({
      ...prevData,
      phoneNumber: value,
    }));

    // Clear the phone number error when the user starts typing
    setErrors((prevErrors) => ({
      ...prevErrors,
      phoneNumber: "", // Clear phone number error when user starts typing
    }));
  };

  const handleStateChange = (e) => {
    const selectedState = e.target.value;

    setFormData((prevData) => ({
      ...prevData,
      state: selectedState,
      city: "",
    }));

    const stateCities = City.getCitiesOfState("US", selectedState);
    setCities(stateCities);

    setErrors((prevErrors) => ({
      ...prevErrors,
      state: "",
      city: "",
    }));
  };

  const handleCityChange = (e) => {
    const selectedCity = e.target.value;
    setFormData((prevData) => ({
      ...prevData,
      city: selectedCity,
    }));

    setErrors((prevErrors) => ({
      ...prevErrors,
      city: "",
    }));
  };

  const convertUrlToFile = async (url) => {
    const response = await fetch(url);
    const blob = await response.blob();
    return new File([blob], "profile.jpg", { type: blob.type });
  };

  const validateField = (name, value) => {
    const validators = {
      firstName: validateFirstName,
      lastName: validateLastName,
      phoneNumber: validatePhoneNumber,
      address: validateAddress,
      zipcode: validateZipcode,
    };

    return validators[name] ? validators[name](value) : "";
  };

  const validateFirstName = (value) => {
    if (!value) return "First name is required";
    if (value.length < 2) return "First name must be at least 2 characters";
    if (value.length > 25) return "First name must be at most 25 characters";
    if (/\d/.test(value)) return "First name must not contain numbers";
    return "";
  };

  const validateLastName = (value) => {
    if (!value) return "Last name is required";
    if (value.length < 2) return "Last name must be at least 2 characters";
    if (value.length > 25) return "Last name must be at most 25 characters";
    if (/\d/.test(value)) return "Last name must not contain numbers";
    return "";
  };

  const validatePhoneNumber = (value) => {
    if (!value) return "Phone number is required";
    if (!/^\(\d{3}\) \d{3}-\d{4}$/.test(value))
      return "Phone Number must be in the format (123) 456-7890";
    return "";
  };

  const validateAddress = (value) => {
    if (!value) return "Address is required";
    if (value.length < 2) return "Address must be at least 2 characters";
    return "";
  };

  const validateZipcode = (value) => {
    if (!value) return "Zipcode is required";
    if (!/^\d{5}(-\d{4})?$/.test(value)) return "Invalid Zipcode format";
    return "";
  };

  // Add this function after the validateZipcode function
  const hasFormChanges = () => {
    const initialData = {
      firstName: profileData.first_name || "",
      lastName: profileData.last_name || "",
      phoneNumber: profileData.phone_number || "",
      address: profileData.address || "",
      state: profileData.state || "",
      city: profileData.city || "",
      zipcode: profileData.zipcode || "",
      brokerageName: profileData.brokerage_name || "",
      sub_vendor: profileData.sub_vendor || "",
      vendor: profileData.vendor || "",
    };

    // Check if any field is different from initial data
    return Object.keys(initialData).some(key => formData[key] !== initialData[key]) ||
      (imageFile && isClicked); // Also check if image has changed
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!hasFormChanges()) {
      toast.info("No changes to save");
      return;
    }
    setLoading(true)

    const newErrors = {};
    Object.keys(formData).forEach((field) => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
      }
    });

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      toast.error("Please correct the errors before submitting.");
      setLoading(false)
      return;
    }

    const finaldata = new FormData();
    finaldata.append("first_name ", formData.firstName);
    finaldata.append("last_name", formData.lastName);
    finaldata.append("phone_number", formData.phoneNumber);
    finaldata.append("address", formData.address);
    finaldata.append("state", formData.state);
    finaldata.append("city", formData.city);
    finaldata.append("zipcode", formData.zipcode);

    if (userType === "Vendor") {
      finaldata.append("brokerage_name", formData.brokerageName || "");
      finaldata.append("sub_vendor", formData.sub_vendor || "");
    }

    // Check if the profilePic is a URL or a file
    if (profilePic.startsWith("https")) {
      try {
        const file = await convertUrlToFile(profilePic); // Define convertUrlToFile separately
        finaldata.append("image", file);
      } catch (err) {
        console.error("Error converting URL to file:", err);
      }
    } else if (imageFile instanceof File) {
      finaldata.append("image", imageFile);
    }

    dispatch(
      updateProfileApi({
        profileId: profileData.id,
        formData: finaldata,
      })
    )
      .unwrap()
      .then((res) => {
        toast.success("Profile Updated successfully!");
        setLoading(false)
        localStorage.setItem("user_first_name", res?.data?.first_name)
        localStorage.setItem("user_last_name", res?.data?.last_name)
        // Dispatch the action to update the state
        if (userType === CommunityOwner) {
          setTimeout(() => {
            navigate("/admin/admin-profile");
          }, 1000);
        }
        if (userType === Realtor) {
          setTimeout(() => {
            navigate("/realtor/realtor-profile");
          }, 1000);
        }
        if (userType === Vendor) {
          setTimeout(() => {
            navigate("/vendor/vendor-profile");
          }, 1000);
        }
        if (userType === HomeOwner) {
          setTimeout(() => {
            navigate("/home-owner-admin/homeowner-profile");
          }, 1000);
        }
      })
      .catch((err) => {
        toast.error(" Profile Updated failed!");
        setLoading(false)
      });
  };

  // Function to handle the image change
  const updateProfilePic = (event) => {
    const file = event.target.files[0]; // Get the selected file
    const reader = new FileReader(); // Create a FileReader to read the file

    reader.onloadend = () => {
      setProfilePic(reader.result); // Set the profile picture state to the selected image
      setIsClicked(true);
      setImageFile(file);
      setShowCropModal(true);
    };

    if (file) {
      reader.readAsDataURL(file); // Read the file as a data URL
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);

      if (!croppedAreaPixels || !profilePic) {
        toast.error('Please select an area to crop');
        return;
      }

      const croppedImageBlob = await getCroppedImg(profilePic, croppedAreaPixels);

      // Create a File object from the blob
      const timestamp = new Date().getTime();
      const croppedImageFile = new File(
        [croppedImageBlob],
        `profile_${timestamp}.jpg`,
        { type: 'image/jpeg' }
      );

      // Update the state with cropped image
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePic(reader.result);
        setImageFile(croppedImageFile);
        setShowCropModal(false);
        setIsSaving(false);
      };
      reader.readAsDataURL(croppedImageFile);

    } catch (error) {
      console.error('Error cropping image:', error);
      toast.error('Error cropping image');
      setIsSaving(false);
    }
  };
  const vendorHandleChange = (e) => {
    const selectedValue = e.target.value;
    setSelectedVendor(selectedValue);
    dispatch(fetchVendorSubTypeList({ vendor_master_name: selectedValue }));
  };

  const handleCloseCropModal = () => {
    setShowCropModal(false);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  return (
    <>
      <div className="back-btn">
        <button onClick={() => navigate(-1)} className="back-btn-button">
          <ArrowBackIcon className="left-icon" />
          Back
        </button>
      </div>
      <div className="container mt-5">
        <div className="row justify-content-center mb-5">
          <div className="col-md-8">
            <div className="card shadow-sm">
              <div className="card-body p-4">
                <h4 className="card-title text-center mb-4">Update Profile</h4>
                <div className="profile-picture-block text-center mb-5">
                  <div className="image-container image-container-profile">
                    <img src={
                      isClicked
                        ? profilePic
                        : formData.image || defaultProfilePic
                    } alt="Profile"
                      className="logo" />
                    <label className="edit-icon" aria-label="Edit Image">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width={32} height={32}>
                        <path d="M21.731 2.269a2.625 2.625 0 0 0-3.712 0l-1.157 1.157 3.712 3.712 1.157-1.157a2.625 2.625 0 0 0 0-3.712ZM19.513 8.199l-3.712-3.712-8.4 8.4a5.25 5.25 0 0 0-1.32 2.214l-.8 2.685a.75.75 0 0 0 .933.933l2.685-.8a5.25 5.25 0 0 0 2.214-1.32l8.4-8.4Z" />
                        <path d="M5.25 5.25a3 3 0 0 0-3 3v10.5a3 3 0 0 0 3 3h10.5a3 3 0 0 0 3-3V13.5a.75.75 0 0 0-1.5 0v5.25a1.5 1.5 0 0 1-1.5 1.5H5.25a1.5 1.5 0 0 1-1.5-1.5V8.25a1.5 1.5 0 0 1 1.5-1.5h5.25a.75.75 0 0 0 0-1.5H5.25Z" />
                      </svg>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={updateProfilePic}
                        style={{ display: "none" }}
                      />
                    </label>
                  </div>
                </div>
                <form onSubmit={handleSubmit} noValidate>
                  <div className="row mb-3">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="firstName" className="form-label">
                        First Name<span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${errors.firstName ? "is-invalid" : ""
                          }`}
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                      />
                      <div className="invalid-feedback">{errors.firstName}</div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="lastName" className="form-label">
                        Last Name<span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${errors.lastName ? "is-invalid" : ""
                          }`}
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                      />
                      <div className="invalid-feedback">{errors.lastName}</div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="phoneNumber" className="form-label">
                        Phone Number<span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${errors.phoneNumber ? "is-invalid" : ""
                          }`}
                        id="phoneNumber"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handlePhoneNumberChange}
                      />
                      <div className="invalid-feedback">
                        {errors.phoneNumber}
                      </div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="address" className="form-label">
                        Address<span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${errors.address ? "is-invalid" : ""
                          }`}
                        id="address"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                      />
                      <div className="invalid-feedback">{errors.address}</div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="state" className="form-label">
                        State<span className="text-danger">*</span>
                      </label>
                      <div className="city-name-blk">
                        <select
                          id="state"
                          name="state"
                          className="dropdown-box webcard-edit-dropdown"
                          value={formData.state}
                          onChange={handleStateChange}
                        >
                          <option value="" disabled selected>
                            Select state
                          </option>
                          {states.map((state) => (
                            <option key={state.id} value={state.isoCode}>
                              {" "}
                              {state.name}
                            </option>
                          ))}
                        </select>
                        {errors.state && (
                          <div className="error-text">{errors.state}</div>
                        )}
                      </div>
                      <div className="invalid-feedback">{errors.state}</div>
                    </div>
                    <div className="col-md-6 mb-3 community-city-blk">
                      <label htmlFor="firstName" className="form-label">
                        City
                      </label>
                      <div className="city-name-blk">
                        <select
                          id="city"
                          name="city"
                          className="dropdown-box webcard-edit-dropdown"
                          value={formData.city}
                          onChange={handleCityChange}
                        >
                          <option value="" disabled selected>
                            Select city
                          </option>
                          {cities.map((city) => (
                            <option key={city.id} value={city.isoCode}>
                              {city.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="zipcode" className="form-label">
                        Zip Code<span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${errors.zipcode ? "is-invalid" : ""
                          }`}
                        id="zipcode"
                        name="zipcode"
                        value={formData.zipcode}
                        onChange={handleChange}
                      />
                      <div className="invalid-feedback">{errors.zipcode}</div>
                    </div>
                    {userType === "Vendor" && (
                      <>
                        {/* Business Name */}
                        <div className="col-md-6 mb-3">
                          <label htmlFor="brokerageName" className="form-label">
                            Buisness Name<span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className={`form-control ${errors.brokerageName ? "is-invalid" : ""}`}
                            id="brokerageName"
                            name="brokerageName"
                            value={formData.brokerageName}
                            onChange={handleChange}
                          />
                        </div>

                        {/* Vendor Type */}
                        <div className="col-md-6 mb-3 city-name-blk">
                          <label htmlFor="vendor-type-select" className="form-label">
                            Vendor Type <span className="text-danger">*</span>
                          </label>
                          {/* <select
        className="city-name-blk"
        id="vendor-type-select"
        name="vendorType"
        value={selectedVendor}
        onChange={vendorHandleChange}
      > */}
                          <option value="" disabled className="vendor-select">
                            {removeUnderScore(formData.vendor ? formData.vendor : "Select Vendor Type")}
                          </option>
                          {/* {Object.entries(vendorMaster).map(
        ([key, label]) => (
          <option key={key} value={key}>
            {label}
          </option>
        )
      )} */}
                          {/* </select> */}
                        </div>

                        {/* Subcategory */}
                        {selectedVendor && (
                          <div className="col-md-6 mb-3 city-name-blk">
                            <label htmlFor="sub-vendor-type-select" className="form-label">
                              Subcategory <span className="text-danger">*</span>
                            </label>
                            <select
                              className="dropdown-box webcard-edit-dropdown"
                              id="sub-vendor-type-select"
                              name="sub_vendor"
                              value={formData.sub_vendor}
                              onChange={(e) => {
                                const selectedValue = e.target.value;
                                setFormData((prev) => ({ ...prev, sub_vendor: selectedValue }));
                              }}
                            // disabled={loading || error}
                            >
                              <option value="" disabled>
                                {loading
                                  ? "Loading..."
                                  : error
                                    ? "Error fetching data"
                                    : "Select Subcategory"}
                              </option>
                              {!loading &&
                                !error &&
                                subVendors.length > 0 &&
                                subVendors.map((sub) => (
                                  <option key={sub.id} value={sub.id}>
                                    {removeUnderScore(sub.sub_vendor_type)}
                                  </option>
                                ))}
                            </select>
                          </div>
                        )}
                      </>
                    )}

                  </div>
                  {/* <div className="mb-3">
                    <label htmlFor="fileInput" className="form-label">
                      Profile Image or Company Logo (Optional)
                    </label>
                    <input
                      type="file"
                      className="form-control"
                      id="fileInput"
                      name="image"
                      accept="image/*"
                      onChange={handleChange}
                    />
                  </div> */}

                  <div className="text-center profile-update">
                    <button
                      type="submit"
                      className="bttn-upload"
                      disabled={loading || !hasFormChanges()}
                    >
                      {loading ? "Submitting..." : "Submit"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Modal show={showCropModal} onHide={handleCloseCropModal}>
        <Modal.Header closeButton>
          <Modal.Title>Crop Image</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ position: "relative", width: "100%", height: "300px" }}>
            {profilePic && (
              <ReactCrop
                image={profilePic}
                crop={crop}
                zoom={zoom}
                minWidth={400}
                minHeight={400}
                aspect={1}
                cropShape="round"
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={(croppedArea, croppedAreaPixels) => {
                  setCroppedAreaPixels(croppedAreaPixels);
                }}
              />
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="bttn-close" onClick={handleCloseCropModal}>
            Close
          </button>
          <button
            className="bttn-upload"
            onClick={handleSave}
            disabled={isSaving || !croppedAreaPixels}
          >
            Save Cropped Image
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default UpdateProfile;